// 1. Imports React
import { useEffect, useState } from "react";

// 2. Imports externes
import { Check, Copy, Key, Lock, TabletSmartphone } from "lucide-react";

// 3. Imports composants UI
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { showSuccessToast, showErrorToast } from "@/components/ui/toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

// 4. Imports hooks personnalisés
import { useAuth } from "@/contexts/AuthContext";
import { useCompany, useUpdateCompany } from "@/hooks/useCompanies";
import type { UpdateCompanyData } from "@/types/company.type";

// 5. Composant
export const MyCompanyPage = () => {
  // ========== A. Custom Hooks ==========
  const { user } = useAuth();
  const companyId = user?.companyId;

  const {
    data: companyData,
    isLoading: loading,
    error: queryError,
  } = useCompany(companyId || "", { enabled: !!companyId });

  const company = companyData?.company || null;

  const { mutate: updateCompany, isPending: isSaving } = useUpdateCompany();

  // ========== B. États locaux ==========
  const [copiedLogin, setCopiedLogin] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [showSessionsOnTablet, setShowSessionsOnTablet] = useState(true);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (company?.pointeuseShowSessions !== undefined) {
      setShowSessionsOnTablet(company.pointeuseShowSessions);
    }
  }, [company?.id, company?.pointeuseShowSessions]);

  // ========== C. Fonctions utilitaires ==========
  const copyToClipboard = async (text: string, type: "login" | "password") => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "login") {
        setCopiedLogin(true);
        setTimeout(() => setCopiedLogin(false), 2000);
      } else {
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
      }
      showSuccessToast(
        `${
          type === "login" ? "Login" : "Mot de passe"
        } copié dans le presse-papiers`
      );
    } catch (error) {
      console.error("Erreur lors de la copie:", error);
    }
  };

  const handleSavePointeuseSettings = () => {
    if (!company) return;

    if (newPassword || confirmPassword) {
      if (newPassword !== confirmPassword) {
        showErrorToast("Les mots de passe ne correspondent pas.");
        return;
      }
      if (newPassword.trim().length < 6) {
        showErrorToast(
          "Le mot de passe doit contenir au moins 6 caractères."
        );
        return;
      }
    }

    const data: UpdateCompanyData = {
      pointeuseShowSessions: showSessionsOnTablet,
    };
    if (newPassword.trim()) {
      data.pointeusePassword = newPassword.trim();
    }

    updateCompany(
      { id: company.id, data },
      {
        onSuccess: () => {
          showSuccessToast("Paramètres de la pointeuse enregistrés.");
          setNewPassword("");
          setConfirmPassword("");
        },
        onError: (err: Error) => {
          showErrorToast(err.message || "Erreur lors de l'enregistrement.");
        },
      }
    );
  };

  // ========== D. Gestion des erreurs et états de chargement ==========
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
        </div>

        <Card className="p-4 sm:p-6">
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (queryError || !company) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-4 sm:p-6">
        <TabletSmartphone className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold text-muted-foreground mb-2">
          Erreur de chargement
        </h2>
        <p className="text-sm text-muted-foreground text-center">
          {queryError
            ? "Une erreur est survenue lors du chargement des identifiants de la pointeuse."
            : "Aucune entreprise trouvée."}
        </p>
      </div>
    );
  }

  // ========== E. JSX ==========
  const pointeuseLogin = company?.pointeuseLogin || null;
  const pointeusePassword = company?.pointeusePasswordPlain || null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <TabletSmartphone className="h-8 w-8 text-primary" />
            Ma Pointeuse
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">
            Consultez et modifiez les paramètres de votre tablette de pointage
          </p>
        </div>
      </div>

      <Card className="p-4 sm:p-6">
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">
            Identifiants de connexion
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Key className="h-4 w-4" />
                Login Pointeuse
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 border-b-2 bg-muted/30 rounded">
                  <p className="text-sm font-mono font-medium">
                    {pointeuseLogin || "Non configuré"}
                  </p>
                </div>
                {pointeuseLogin && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(pointeuseLogin, "login")}
                    className="shrink-0"
                  >
                    {copiedLogin ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Mot de passe actuel
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 border-b-2 bg-muted/30 rounded">
                  <p className="text-sm font-medium">
                    {pointeusePassword || "Non configuré"}
                  </p>
                </div>
                {pointeusePassword && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      copyToClipboard(pointeusePassword, "password")
                    }
                    className="shrink-0"
                  >
                    {copiedPassword ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {(!pointeuseLogin || !pointeusePassword) && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Les identifiants de la pointeuse ne sont pas encore
                configurés. Contactez un super utilisateur pour les activer.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="p-4 sm:p-6">
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">
            Paramètres de la pointeuse
          </CardTitle>
          <p className="text-sm text-muted-foreground font-normal pt-1">
            Ces réglages s’appliquent à l’application installée sur la
            tablette. Après un changement de mot de passe, reconnectez la
            pointeuse avec le nouveau mot de passe.
          </p>
        </CardHeader>
        <CardContent className="space-y-6 pt-2">
          {/* <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-lg border p-4">
            <div className="space-y-1">
              <Label htmlFor="show-sessions" className="text-base font-medium">
                Afficher l’historique des pointages
              </Label>
              <p className="text-sm text-muted-foreground">
                Si cette option est désactivée, la liste des pointages n’apparaît
                plus sur la tablette (les pointages restent enregistrés côté
                serveur).
              </p>
            </div>
            <Switch
              id="show-sessions"
              checked={showSessionsOnTablet}
              onCheckedChange={setShowSessionsOnTablet}
            />
          </div> */}

          <div className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="new-pw">Nouveau mot de passe (optionnel)</Label>
              <Input
                id="new-pw"
                type="password"
                autoComplete="new-password"
                placeholder="Laisser vide pour ne pas changer"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-pw">Confirmer le mot de passe</Label>
              <Input
                id="confirm-pw"
                type="password"
                autoComplete="new-password"
                placeholder="Répétez le nouveau mot de passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <Button
            type="button"
            onClick={handleSavePointeuseSettings}
            disabled={isSaving}
          >
            {isSaving ? "Enregistrement…" : "Enregistrer les paramètres"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
